import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import API from '../services/api';
import './ManagerReview.css';

const ManagerReview = () => {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth(); // Get auth info from context
  const [incident, setIncident] = useState(null);
  const [investigation, setInvestigation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [notification, setNotification] = useState({ type: null, message: '' });
  const [feedback, setFeedback] = useState({ findings: '', recommendations: '' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const fetchIncidentDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch incident details from the backend
      const incidentData = await API.get(`/api/incidents/${incidentId}`);

      if (incidentData.data && incidentData.data.incident) {
        setIncident(incidentData.data.incident);
        // Ensure DELEGATE_FINDINGS and DELEGATE_RECOMMENDATIONS are included
        console.log('Incident details:', incidentData.data.incident);
      }
    } catch (error) {
      console.error('Error fetching incident details:', error);
      setError(`Failed to fetch incident: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => {
    console.log('Authenticated as:', user?.USERNAME || user?.EMAIL || 'undefined');
  }, [user]);

  useEffect(() => {
    // Check authentication first
    if (!isAuthenticated) {
      console.log('Not authenticated, redirecting to login');
      navigate('/login', { state: { from: `/incidents/${incidentId}/review` } });
      return;
    }
    
    console.log('Authenticated as:', user?.USERNAME || user?.EMAIL);
    fetchIncidentDetails();
  }, [fetchIncidentDetails, incidentId, isAuthenticated, navigate, user?.EMAIL, user?.USERNAME]);

  const saveInvestigation = async () => {
    try {
      if (!investigation || !incidentId) return;

      const response = await API.put(`/api/incidents/${incidentId}/investigation/${investigation.ID}`, {
        ...investigation,
        UPDATED_BY: user?.username || user?.email
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to save investigation');
      }

      setInvestigation(response.data.investigation);
      setHasChanges(false);
      return true;
    } catch (error) {
      console.error('Error saving investigation:', error);
      setNotification({
        type: 'error',
        message: `Failed to save investigation: ${error.message}`
      });
      return false;
    }
  };

  const handleApproveInvestigation = async () => {
    try {
      setSubmitting(true);
      
      // First save any pending changes to the investigation
      if (hasChanges) {
        await saveInvestigation();
      }
      
      // Now send the proper status update request with all required fields
      const response = await API.put(`/api/incidents/${incidentId}/status`, {
        status: 'SUPERVISOR_APPROVED', 
        role: user?.role || 'SUPERVISOR',  // Make sure user role is provided
        notes: reviewNotes,
        userEmail: user?.username || user?.email  // Make sure user email is provided
      });
      
      if (response.data && response.data.success) {
        // Update local state
        setInvestigation(response.data.investigation);
        setNotification({
          type: 'success',
          message: 'Investigation successfully approved'
        });
        
        // Reload data to reflect changes
        fetchIncidentDetails();
      } else {
        throw new Error(response.data?.message || 'Failed to approve investigation');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      setNotification({
        type: 'error',
        message: error.response?.data?.message || 'Failed to approve investigation. Please try again later.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveClick = () => {
    setShowConfirmModal(true);
  };

  const confirmApproval = async () => {
    setShowConfirmModal(false);
    await handleApproveInvestigation();
  };

  const clearNotification = () => {
    setNotification({ type: null, message: '' });
  };

  const debugFetchIncident = async () => {
    try {
      setError("Starting direct fetch test...");
      
      const response = await API.get(`/api/incidents/${incidentId}`);
      
      setError(`Fetch status: ${response.status} ${response.statusText}`);
      
      const data = response.data;
      console.log('Direct fetch response:', data);
      
      if (data.success) {
        setIncident(data.incident);
        setError("Direct fetch successful! Check console for details.");
      } else {
        setError(`Server error: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Direct fetch error:', error);
      setError(`Direct fetch failed: ${error.message}`);
    }
  };

  if (!isAuthenticated) {
    return <div>You are not authorized to view this page.</div>;
  }

  if (loading) {
    return <div className="loading-container">Loading investigation...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error Loading Investigation</h2>
        <p>{error}</p>
        <div className="error-actions">
          <button onClick={fetchIncidentDetails} className="retry-button">
            Retry
          </button>
          <button onClick={() => navigate('/supervisor-dashboard')} className="back-button">
            Back to Dashboard
          </button>
          {/* Add debug info for development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="debug-info">
              <h3>Debug Info</h3>
              <p>Incident ID: {incidentId}</p>
              <p>User: {user?.USERNAME || user?.EMAIL || 'No user'}</p>
              <p>Role: {user?.role || 'No role'}</p>
            </div>
          )}
          <button 
            onClick={debugFetchIncident} 
            className="debug-button"
          >
            Debug Fetch
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manager-review-container">
      {notification.message && (
        <div className={`notification ${notification.type}`}>
          <span>{notification.message}</span>
          <button onClick={clearNotification}>×</button>
        </div>
      )}

      <div className="review-header">
        <h1>Manager Review</h1>
        <p>Welcome, {user?.USERNAME || user?.EMAIL || 'User'}!</p>
        <div className="status-badge">
          {investigation?.INVESTIGATION_STATUS || 'Pending'}
        </div>
      </div>

      <div className="incident-summary">
        <h2>Incident Summary</h2>
        <div className="summary-details">
          <p><strong>Title:</strong> {incident?.TITLE}</p>
          <p><strong>Date:</strong> {incident?.INCIDENT_DATE ? new Date(incident.INCIDENT_DATE).toLocaleDateString() : 'Unknown'}</p>
          <p><strong>Department:</strong> {incident?.DEPARTMENT}</p>
          <p><strong>Description:</strong> {incident?.DESCRIPTION}</p>
        </div>

        {/* Add Delegate Findings and Recommendations */}
        <div className="delegate-section">
          <h3>Delegate Findings</h3>
          <p>{incident?.DELEGATE_FINDINGS || 'No findings provided by the delegate.'}</p>
          <textarea
            placeholder="Add feedback on findings..."
            value={feedback.findings}
            onChange={(e) => setFeedback({ ...feedback, findings: e.target.value })}
          />

          <h3>Delegate Recommendations</h3>
          <p>{incident?.DELEGATE_RECOMMENDATIONS || 'No recommendations provided by the delegate.'}</p>
          <textarea
            placeholder="Add feedback on recommendations..."
            value={feedback.recommendations}
            onChange={(e) => setFeedback({ ...feedback, recommendations: e.target.value })}
          />
        </div>
      </div>

      <div className="review-form">
        <h2>Review Notes</h2>
        <textarea 
          value={reviewNotes} 
          onChange={(e) => setReviewNotes(e.target.value)}
          placeholder="Add your review notes here..."
          disabled={submitting}
        />
        
        <div className="action-buttons">
          <button 
            onClick={() => navigate('/manager-dashboard')} 
            className="cancel-button"
            disabled={submitting}
          >
            Back to Dashboard
          </button>
          
          <button onClick={handleApproveClick} className="approve-button" disabled={submitting}>
            {submitting ? 'Processing...' : 'Approve Investigation'}
          </button>
        </div>
      </div>

      {showConfirmModal && (
        <div className="modal">
          <p>Are you sure you want to approve this investigation?</p>
          <button onClick={confirmApproval}>Yes</button>
          <button onClick={() => setShowConfirmModal(false)}>No</button>
        </div>
      )}
    </div>
  );
};

export default ManagerReview;